<script setup lang="ts">
import { computed, ref, defineComponent, h, watch } from 'vue';
import { Input } from '@/components/ui/input';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/vue/24/solid';
import { Button } from '@/components/ui/button';

const props = defineProps<{
  field: string;
  dataType: string;
  data: any;
  classType: string;
}>();

const filterText = ref('');
const viewMode = ref<'grouped' | 'flat'>('grouped');
const sortMode = ref<'position' | 'alphabetical'>('position');

// Get the children array data
// Field pattern: 'bucket_tag_excludes.display_string' where 'bucket_tag_excludes' is the array field
const childrenArray = computed(() => {
  const parts = props.field.split('.');
  if (parts.length !== 2 || parts[1] !== 'display_string') {
    return [];
  }
  const arrayField = parts[0];
  const arrayData = props.data[arrayField];
  return Array.isArray(arrayData) ? arrayData : [];
});

// Build tag hierarchy
const tagHierarchy = computed(() => {
  const tags = childrenArray.value;
  if (!tags || tags.length === 0) {
    return [];
  }

  // Create a map of tags by id for quick lookup
  const tagMap = new Map<number, any>();
  tags.forEach((tag: any) => {
    tagMap.set(tag.id, tag);
  });

  // Build parent-child relationships
  // Group tags by their parent_tag_id (including null for root tags)
  const childrenByParent = new Map<number | null, any[]>();
  // Track parent tag data for header rows
  const parentTagData = new Map<number, any>();

  tags.forEach((tag: any) => {
    const parentId = tag.parent_tag_id || null;
    
    // If tag has a parent_tag subdocument, store it for header row
    if (tag.parent_tag && tag.parent_tag.id) {
      parentTagData.set(tag.parent_tag.id, tag.parent_tag);
    }
    
    if (!childrenByParent.has(parentId)) {
      childrenByParent.set(parentId, []);
    }
    childrenByParent.get(parentId)!.push(tag);
  });

  // Build hierarchy structure
  const buildHierarchy = (parentId: number | null): any[] => {
    const children = childrenByParent.get(parentId) || [];
    const sortedChildren = sortTags(children);
    
    return sortedChildren.map((tag: any) => {
      const childTags = buildHierarchy(tag.id);
      return {
        ...tag,
        children: childTags,
        hasChildren: childTags.length > 0,
      };
    });
  };

  // Build root level: include both root tags (null parent) and parent header rows
  const rootTags = buildHierarchy(null);
  const result: any[] = [];

  // Group tags by their parent to create header rows
  const tagsByParent = new Map<number | null, any[]>();
  tags.forEach((tag: any) => {
    const parentId = tag.parent_tag_id || null;
    if (!tagsByParent.has(parentId)) {
      tagsByParent.set(parentId, []);
    }
    tagsByParent.get(parentId)!.push(tag);
  });

  // Create header rows for parent tags that aren't in the array
  const processedParents = new Set<number>();
  
  tags.forEach((tag: any) => {
    if (tag.parent_tag_id && !tagMap.has(tag.parent_tag_id) && !processedParents.has(tag.parent_tag_id)) {
      processedParents.add(tag.parent_tag_id);
      const parentData = parentTagData.get(tag.parent_tag_id);
      if (parentData) {
        // Create a virtual parent header row
        const parentChildren = tagsByParent.get(tag.parent_tag_id) || [];
        const sortedParentChildren = sortTags(parentChildren);
        const nestedChildren = sortedParentChildren.map((childTag: any) => {
          const childTags = buildHierarchy(childTag.id);
          return {
            ...childTag,
            children: childTags,
            hasChildren: childTags.length > 0,
          };
        });
        
        result.push({
          id: parentData.id,
          name: parentData.name || parentData.display_string,
          display_string: parentData.display_string,
          is_header_tag: true,
          parent_tag_id: null,
          position: parentData.position || 0,
          children: nestedChildren,
          hasChildren: nestedChildren.length > 0,
          isVirtualParent: true, // Flag to indicate this is a virtual parent header
        });
      }
    }
  });

  // Add root tags (tags without parents or with parents that are in the array)
  result.push(...rootTags);

  // Sort the final result
  return sortTags(result);
});

// Sort tags based on current sort mode
const sortTags = (tags: any[]): any[] => {
  if (sortMode.value === 'alphabetical') {
    return [...tags].sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  } else {
    // Sort by position, then by name
    return [...tags].sort((a, b) => {
      const posA = a.position || 0;
      const posB = b.position || 0;
      if (posA !== posB) {
        return posA - posB;
      }
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }
};

// Flatten hierarchy for flat view - show all tags regardless of hierarchy
const flatTags = computed(() => {
  // For flat view, we want to show all tags, not just the hierarchy
  // So we'll flatten the hierarchy but also include tags that might not be in the hierarchy
  const tags = childrenArray.value;
  if (!tags || tags.length === 0) {
    return [];
  }
  
  // Sort all tags
  const sortedTags = sortTags(tags);
  
  // Build a map of parent relationships for indentation
  const tagMap = new Map<number, any>();
  tags.forEach((tag: any) => {
    tagMap.set(tag.id, tag);
  });
  
  // Calculate level for each tag
  const getLevel = (tag: any, level = 0): number => {
    if (!tag.parent_tag_id || !tagMap.has(tag.parent_tag_id)) {
      return level;
    }
    const parent = tagMap.get(tag.parent_tag_id);
    return getLevel(parent, level + 1);
  };
  
  return sortedTags.map((tag: any) => ({
    ...tag,
    level: getLevel(tag),
  }));
});

// Filter tags based on filter text
const filteredHierarchy = computed(() => {
  if (!filterText.value.trim()) {
    return tagHierarchy.value;
  }
  const searchText = filterText.value.toLowerCase();
  
  const filterRecursive = (items: any[]): any[] => {
    return items
      .filter((item: any) => {
        const matches = (item.name || '').toLowerCase().includes(searchText);
        const childrenMatch = item.children && filterRecursive(item.children).length > 0;
        return matches || childrenMatch;
      })
      .map((item: any) => ({
        ...item,
        children: item.children ? filterRecursive(item.children) : [],
      }));
  };
  
  return filterRecursive(tagHierarchy.value);
});

const filteredFlatTags = computed(() => {
  if (!filterText.value.trim()) {
    return flatTags.value;
  }
  const searchText = filterText.value.toLowerCase();
  return flatTags.value.filter((tag: any) =>
    (tag.name || '').toLowerCase().includes(searchText)
  );
});

// Track expanded state for grouped view - default to all parents expanded
const expandedParents = ref<Set<number>>(new Set());

// Initialize expanded parents when hierarchy changes
const initializeExpandedParents = () => {
  const expanded = new Set<number>();
  const addParents = (items: any[]) => {
    items.forEach((item: any) => {
      if (item.hasChildren) {
        expanded.add(item.id);
        if (item.children && item.children.length > 0) {
          addParents(item.children);
        }
      }
    });
  };
  addParents(tagHierarchy.value);
  expandedParents.value = expanded;
};

// Watch tagHierarchy and initialize expanded parents
watch(tagHierarchy, () => {
  initializeExpandedParents();
}, { immediate: true });

const toggleParent = (tagId: number) => {
  if (expandedParents.value.has(tagId)) {
    expandedParents.value.delete(tagId);
  } else {
    expandedParents.value.add(tagId);
  }
};

// Recursive component for tree items
const TagTreeItem = defineComponent({
  name: 'TagTreeItem',
  props: {
    tag: { type: Object, required: true },
    expandedParents: { type: Object as () => Set<number>, required: true },
    level: { type: Number, default: 0 },
  },
  emits: ['toggle'],
  setup(props, { emit }) {
    const isExpanded = computed(() => props.expandedParents.has(props.tag.id));
    const toggle = () => {
      if (props.tag.hasChildren) {
        emit('toggle', props.tag.id);
      }
    };
    
    return () => {
      const children = props.tag.hasChildren && isExpanded.value
        ? props.tag.children.map((child: any) =>
            h(TagTreeItem, {
              key: child.id,
              tag: child,
              expandedParents: props.expandedParents,
              level: props.level + 1,
              onToggle: (id: number) => emit('toggle', id),
            })
          )
        : null;
      
      return h('div', { class: 'select-none' }, [
        h('div', {
          class: [
            'flex items-center gap-1 px-2 py-1.5 text-sm hover:bg-muted rounded-md',
            (props.tag.is_header_tag || props.tag.hasChildren) && 'font-semibold',
            props.tag.hasChildren && 'cursor-pointer',
          ],
          style: { paddingLeft: `${8 + props.level * 16}px` },
          onClick: toggle,
        }, [
          props.tag.hasChildren
            ? h('button', {
                class: 'flex items-center justify-center w-4 h-4',
                onClick: (e: Event) => {
                  e.stopPropagation();
                  emit('toggle', props.tag.id);
                },
              }, [
                isExpanded.value
                  ? h(ChevronDownIcon, { class: 'h-3 w-3 transition-transform' })
                  : h(ChevronRightIcon, { class: 'h-3 w-3 transition-transform' }),
              ])
            : h('span', { class: 'w-4' }),
          h('span', { class: 'flex-1 break-words' }, props.tag.name || '—'),
        ]),
        children ? h('div', {}, children) : null,
      ]);
    };
  },
});

</script>

<template>
  <div class="flex flex-col flex-1 min-h-0 overflow-hidden">
    <!-- Controls: Filter, View Mode, Sort -->
    <div class="py-3 border-b space-y-2 px-4">
      <Input
        v-model="filterText"
        placeholder="Filter tags..."
      />
      <div class="flex items-center gap-2">
        <div class="flex items-center gap-1">
          <Button
            :variant="viewMode === 'grouped' ? 'default' : 'outline'"
            size="sm"
            @click="viewMode = 'grouped'"
            class="h-7 text-xs"
          >
            Grouped
          </Button>
          <Button
            :variant="viewMode === 'flat' ? 'default' : 'outline'"
            size="sm"
            @click="viewMode = 'flat'"
            class="h-7 text-xs"
          >
            Flat
          </Button>
        </div>
        <div class="flex items-center gap-1 ml-auto">
          <Button
            :variant="sortMode === 'position' ? 'default' : 'outline'"
            size="sm"
            @click="sortMode = 'position'"
            class="h-7 text-xs"
          >
            Position
          </Button>
          <Button
            :variant="sortMode === 'alphabetical' ? 'default' : 'outline'"
            size="sm"
            @click="sortMode = 'alphabetical'"
            class="h-7 text-xs"
          >
            A-Z
          </Button>
        </div>
      </div>
    </div>
    <!-- Scrollable content -->
    <div class="flex-1 min-h-0 overflow-y-auto px-4">
      <!-- Grouped/Tree View -->
      <div v-if="viewMode === 'grouped'" class="p-2">
        <template v-if="filteredHierarchy.length > 0">
          <template v-for="tag in filteredHierarchy" :key="tag.id">
            <TagTreeItem
              :tag="tag"
              :expanded-parents="expandedParents"
              @toggle="toggleParent"
            />
          </template>
        </template>
        <div v-else class="px-3 py-6 text-center text-muted-foreground text-sm">
          No items found
        </div>
      </div>
      <!-- Flat List View -->
      <div v-else class="p-2">
        <template v-if="filteredFlatTags.length > 0">
          <div
            v-for="tag in filteredFlatTags"
            :key="tag.id"
            class="px-2 py-1.5 text-sm break-words hover:bg-muted rounded-md"
            :style="{ paddingLeft: `${8 + tag.level * 16}px` }"
          >
            {{ tag.name || '—' }}
          </div>
        </template>
        <div v-else class="px-3 py-6 text-center text-muted-foreground text-sm">
          No items found
        </div>
      </div>
    </div>
  </div>
</template>
